import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Check, UserPlus, AlertCircle } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "",
  plan_id: "",
  duration_mode: "full_season",
  preferred_start_date: "",
  assigned_grower_id: "",
  delivery_address: "", delivery_suburb: "", delivery_city: "", delivery_postcode: "", delivery_notes: "",
  is_gift: false, gift_message: "",
  payment_status: "paid",
  admin_notes: "",
};

const DURATION_OPTIONS = [
  { v: "full_season", label: "Whole season (Jan to Mar)" },
  { v: "january", label: "January only" },
  { v: "february", label: "February only" },
  { v: "march", label: "March only" },
  { v: "single", label: "Single delivery" },
];

const PAYMENT_OPTIONS = [
  { v: "paid", label: "Paid (offline)" },
  { v: "pending", label: "Pending invoice" },
  { v: "comp", label: "Complimentary / Gift" },
];

export default function ManualSubscriberModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/admin/subscription-plans"),
      api.get("/admin/growers"),
    ]).then(([plansRes, growersRes]) => {
      if (cancelled) return;
      setPlans((plansRes.data.plans || []).filter((p) => p.status === "active"));
      setGrowers((growersRes.data.growers || []).filter((g) => g.stage === "active" || g.stage === "approved"));
      setLoadingRefs(false);
    }).catch(() => { if (!cancelled) setLoadingRefs(false); });
    return () => { cancelled = true; };
  }, []);

  const validate = () => {
    if (form.name.trim().length < 2 || !form.email.includes("@")) return "Please give a name and a valid email.";
    if (!form.plan_id) return "Please choose a subscription plan.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validate();
    if (v) return setErr(v);
    setBusy(true);
    try {
      const payload = {
        ...form,
        assigned_grower_id: form.assigned_grower_id || "",
        preferred_start_date: form.preferred_start_date || "",
      };
      const { data } = await api.post("/admin/subscribers/manual", payload);
      setResult(data);
      onCreated?.(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="manual-subscriber-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <div className="flex items-center gap-2">
            <UserPlus size={16} strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Add a Subscriber manually</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="manual-subscriber-close"><X size={16} /></button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="p-6" data-testid="manual-subscriber-form">
            <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-6">
              Create a subscriber account and an active subscription without going through Stripe checkout. Useful for hand-rolled onboarding, gifted seasons, and comp accounts.
            </p>

            {loadingRefs ? (
              <div className="text-center py-8 font-serif italic text-[var(--bloom-ink-muted)]">Loading plans &amp; growers…</div>
            ) : (
              <div className="space-y-5">
                <Section title="Subscriber">
                  <Grid cols={2}>
                    <Field label="Full name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="bloom-input" autoFocus data-testid="manual-sub-name" /></Field>
                    <Field label="Email *"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bloom-input" data-testid="manual-sub-email" /></Field>
                    <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bloom-input" /></Field>
                  </Grid>
                </Section>

                <Section title="Subscription">
                  <Grid cols={2}>
                    <Field label="Plan *">
                      <select value={form.plan_id} onChange={(e) => set("plan_id", e.target.value)} className="bloom-input" data-testid="manual-sub-plan">
                        <option value=""> </option>
                        {plans.map((p) => {
                          const label = `${p.name} · $${p.price_nzd} / ${p.frequency}`;
                          return <option key={p.id} value={p.id}>{label}</option>;
                        })}
                      </select>
                    </Field>
                    <Field label="Duration">
                      <select value={form.duration_mode} onChange={(e) => set("duration_mode", e.target.value)} className="bloom-input" data-testid="manual-sub-duration">
                        {DURATION_OPTIONS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Preferred start date"><input type="date" value={form.preferred_start_date} onChange={(e) => set("preferred_start_date", e.target.value)} className="bloom-input" /></Field>
                    <Field label="Assigned grower (optional)">
                      <select value={form.assigned_grower_id} onChange={(e) => set("assigned_grower_id", e.target.value)} className="bloom-input" data-testid="manual-sub-grower">
                        <option value="">Auto-assign later</option>
                        {growers.map((g) => {
                          const u = g.user || {};
                          const label = `${u.name || "·"} · ${u.farm_name || u.region || ""}`;
                          return <option key={u.id} value={u.id}>{label}</option>;
                        })}
                      </select>
                    </Field>
                  </Grid>
                </Section>

                <Section title="Delivery">
                  <Grid cols={2}>
                    <Field label="Address line"><input value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} className="bloom-input" data-testid="manual-sub-address" /></Field>
                    <Field label="Suburb"><input value={form.delivery_suburb} onChange={(e) => set("delivery_suburb", e.target.value)} className="bloom-input" /></Field>
                    <Field label="City"><input value={form.delivery_city} onChange={(e) => set("delivery_city", e.target.value)} className="bloom-input" /></Field>
                    <Field label="Postcode"><input value={form.delivery_postcode} onChange={(e) => set("delivery_postcode", e.target.value)} className="bloom-input" /></Field>
                  </Grid>
                  <Field label="Delivery notes"><textarea rows={2} value={form.delivery_notes} onChange={(e) => set("delivery_notes", e.target.value)} className="bloom-input" /></Field>
                </Section>

                <Section title="Payment &amp; gift">
                  <Grid cols={2}>
                    <Field label="Payment status">
                      <select value={form.payment_status} onChange={(e) => set("payment_status", e.target.value)} className="bloom-input" data-testid="manual-sub-payment">
                        {PAYMENT_OPTIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Gift subscription">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.is_gift} onChange={(e) => set("is_gift", e.target.checked)} data-testid="manual-sub-gift" />
                        <span className="font-serif italic">This is a gift</span>
                      </label>
                    </Field>
                  </Grid>
                  {form.is_gift && (
                    <Field label="Gift message"><textarea rows={2} value={form.gift_message} onChange={(e) => set("gift_message", e.target.value)} className="bloom-input" /></Field>
                  )}
                </Section>

                <Section title="Admin notes">
                  <Field label="Internal notes (only visible to admins)">
                    <textarea rows={2} value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} className="bloom-input" data-testid="manual-sub-notes" />
                  </Field>
                </Section>
              </div>
            )}

            {err && <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="manual-sub-error"><AlertCircle size={14} /> {err}</div>}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
              <button type="submit" disabled={busy || loadingRefs} className="bloom-btn bloom-btn-primary" data-testid="manual-sub-submit">
                {busy ? "Creating…" : "Create subscription"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-8" data-testid="manual-subscriber-success">
            <Check className="mx-auto mb-3 text-[var(--bloom-ink)]" size={36} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-center mb-1">{result.user.name} subscribed.</h3>
            <p className="text-center font-serif italic text-[var(--bloom-ink-soft)] mb-2">{result.user.email}</p>
            <div className="text-center mb-6">
              <span className="inline-block text-[10px] tracking-[0.2em] uppercase px-3 py-1 border border-[var(--bloom-border)] bg-[var(--bloom-surface)]">
                {result.subscription.plan_name} · {result.subscription.duration_label}
              </span>
            </div>
            {result.newly_created_user && (
              <div className="border border-[var(--bloom-border)] bg-[var(--bloom-surface)] p-5 text-sm font-serif italic text-[var(--bloom-ink-soft)] text-center">
                A new subscriber account was created. They&rsquo;ll receive a password-setup link in their welcome email when SendGrid is wired.
              </div>
            )}
            <div className="mt-6 text-center">
              <button onClick={onClose} className="bloom-btn bloom-btn-primary" data-testid="manual-sub-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3" dangerouslySetInnerHTML={{ __html: title }} />
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
